import React, { useEffect, useState } from "react";
import styles from "./CategoryCard.module.css";
import { getSelectionForQuestion, setSelectionForQuestion } from "./selectionStore";

function buildStableKey(q: CategoryQuestion): string {
    const base = (q.question || "").trim().toLowerCase();
    const choices = (q.choices || []).map(c => (typeof c === "string" ? c : c.label || "").trim().toLowerCase());
    return `q|${base}|c|${choices.join(";")}`;
}

export interface CategoryQuestion {
    question: string;
    choices?: (string | { id: string; label: string; isOther?: boolean; inputPlaceholder?: string })[];
}

interface CategoryCardProps {
    questionObj: CategoryQuestion;
    onAnswerChange?: (selected: string[]) => void;
    clearTriggerId?: number;
}

export const CategoryCard: React.FC<CategoryCardProps> = ({ questionObj, onAnswerChange, clearTriggerId }) => {
    const { question, choices } = questionObj;
    const [selectedChoices, setSelectedChoices] = useState<string[]>([]);
    const [otherEditing, setOtherEditing] = useState<boolean>(false);
    const [otherInputValue, setOtherInputValue] = useState<string>("");
    const [otherSelectedValue, setOtherSelectedValue] = useState<string>("");

    // Hydrate from store on mount
    useEffect(() => {
        const key = buildStableKey(questionObj);
        const saved = getSelectionForQuestion(key);
        if (saved) {
            setSelectedChoices(saved.selectedChoices || []);
            setOtherSelectedValue(saved.otherSelectedValue || "");
        }
        // no deps: run once per mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // React to clear trigger
    useEffect(() => {
        if (clearTriggerId !== undefined && clearTriggerId > 0) {
            setSelectedChoices([]);
            setOtherSelectedValue("");
            setOtherInputValue("");
            setOtherEditing(false);
            // Note: The store is already cleared by the parent calling clearAllSelections()
        }
    }, [clearTriggerId]);

    const handleChoiceClick = (choice: string) => {
        setSelectedChoices(prev => {
            const newSelected = prev.includes(choice) ? prev.filter(c => c !== choice) : [...prev, choice];
            if (onAnswerChange) {
                onAnswerChange(newSelected);
            }
            // Persist selection
            setSelectionForQuestion(buildStableKey(questionObj), {
                selectedChoices: newSelected,
                otherSelectedValue
            });
            return newSelected;
        });
    };

    return (
        <div className={styles.categoryCard}>
            <div className={styles.questionText}>{question}</div>
            {choices && choices.length > 0 && (
                <div className={styles.choicesContainer}>
                    {choices.map((choice, idx) => {
                        const isObject = typeof choice !== "string";
                        const choiceLabel = isObject ? (choice as any).label : (choice as string);
                        const choiceId = isObject ? (choice as any).id : undefined;
                        const isOther = isObject && choiceId === "other";
                        const isSelected = isOther
                            ? !!otherSelectedValue && selectedChoices.includes(otherSelectedValue)
                            : selectedChoices.includes(choiceLabel);

                        if (isOther) {
                            return (
                                <div
                                    key={idx}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                        width: "100%",
                                        flexBasis: "100%",
                                        flexWrap: "wrap"
                                    }}
                                >
                                    {!otherEditing ? (
                                        <button
                                            className={`${styles.choiceButton} ${styles.otherButton} ${isSelected ? styles.selected : ""}`}
                                            data-role="choice-button"
                                            data-selected={isSelected ? "true" : "false"}
                                            onClick={() => {
                                                setOtherInputValue(otherSelectedValue || "");
                                                setOtherEditing(true);
                                                setTimeout(() => {
                                                    const input = document.getElementById(`catcard-other-input-${idx}`) as HTMLInputElement | null;
                                                    input?.focus();
                                                }, 0);
                                            }}
                                        >
                                            <span style={{ fontStyle: otherSelectedValue ? "normal" : "italic" }}>{otherSelectedValue || choiceLabel}</span>
                                            <span className={styles.otherIcon}>✎</span>
                                        </button>
                                    ) : (
                                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", width: "100%" }}>
                                            <div className={styles.otherEditContainer} data-role="other-edit">
                                                <input
                                                    id={`catcard-other-input-${idx}`}
                                                    type="text"
                                                    value={otherInputValue}
                                                    placeholder={(choice as any).inputPlaceholder || choiceLabel}
                                                    onChange={e => setOtherInputValue(e.target.value)}
                                                    onKeyDown={e => {
                                                        if (e.key === "Enter") {
                                                            const trimmed = otherInputValue.trim();
                                                            if (!trimmed) {
                                                                if (otherSelectedValue) {
                                                                    setSelectedChoices(prev => prev.filter(v => v !== otherSelectedValue));
                                                                }
                                                                setOtherSelectedValue("");
                                                                setOtherEditing(false);
                                                                setOtherInputValue("");
                                                                return;
                                                            }
                                                            if (otherSelectedValue) {
                                                                setSelectedChoices(prev => prev.filter(v => v !== otherSelectedValue));
                                                            }
                                                            handleChoiceClick(trimmed);
                                                            setOtherSelectedValue(trimmed);
                                                            setSelectionForQuestion(buildStableKey(questionObj), {
                                                                selectedChoices: selectedChoices.includes(trimmed)
                                                                    ? selectedChoices
                                                                    : [...selectedChoices.filter(v => v !== otherSelectedValue), trimmed],
                                                                otherSelectedValue: trimmed
                                                            });
                                                            setOtherEditing(false);
                                                            setOtherInputValue("");
                                                        } else if (e.key === "Escape") {
                                                            setOtherEditing(false);
                                                        }
                                                    }}
                                                    className={styles.otherInput}
                                                />
                                                {otherInputValue && (
                                                    <button
                                                        type="button"
                                                        title="Clear input"
                                                        onClick={() => {
                                                            setOtherInputValue("");
                                                            const input = document.getElementById(`catcard-other-input-${idx}`) as HTMLInputElement | null;
                                                            input?.focus();
                                                        }}
                                                        className={styles.otherInputClear}
                                                    >
                                                        ✕
                                                    </button>
                                                )}
                                            </div>
                                            <button
                                                className={styles.choiceButton}
                                                onClick={() => {
                                                    const trimmed = otherInputValue.trim();
                                                    if (!trimmed) {
                                                        if (otherSelectedValue) {
                                                            setSelectedChoices(prev => prev.filter(v => v !== otherSelectedValue));
                                                        }
                                                        setOtherSelectedValue("");
                                                        setOtherEditing(false);
                                                        setOtherInputValue("");
                                                        return;
                                                    }
                                                    if (otherSelectedValue) {
                                                        setSelectedChoices(prev => prev.filter(v => v !== otherSelectedValue));
                                                    }
                                                    handleChoiceClick(trimmed);
                                                    setOtherSelectedValue(trimmed);
                                                    setSelectionForQuestion(buildStableKey(questionObj), {
                                                        selectedChoices: selectedChoices.includes(trimmed)
                                                            ? selectedChoices
                                                            : [...selectedChoices.filter(v => v !== otherSelectedValue), trimmed],
                                                        otherSelectedValue: trimmed
                                                    });
                                                    setOtherEditing(false);
                                                    setOtherInputValue("");
                                                }}
                                            >
                                                OK
                                            </button>
                                            <button className={styles.choiceButton} onClick={() => setOtherEditing(false)}>
                                                Cancel
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        }

                        return (
                            <button
                                key={idx}
                                className={`${styles.choiceButton} ${isOther ? styles.otherButton : ""} ${isSelected ? styles.selected : ""}`}
                                data-role="choice-button"
                                data-selected={isSelected ? "true" : "false"}
                                onClick={() => handleChoiceClick(choiceLabel)}
                            >
                                <span style={{ fontStyle: isOther && !otherSelectedValue ? "italic" : "normal" }}>
                                    {isOther ? otherSelectedValue || choiceLabel : choiceLabel}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}
            {/* Note: No submit button here */}
        </div>
    );
};

export default CategoryCard;
